import {
  AGENT_CONFIG_FIELD_LABELS,
  PROMPT_ROLE_LABELS,
  PROMPT_ROLES,
  REQUIRED_PROMPT_ROLES,
  roleAtLeast,
  type AgentConfig,
  type AgentConfigPatch,
  type AgentVersionSummary,
  type HistoryMode,
  type PromptRole,
} from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  bindChannelAgent,
  createAgentVersion,
  fetchAgent,
  fetchAgentDiff,
  fetchChannels,
  fetchPrompts,
  publishAgentVersion,
} from '../api/agents.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ConfirmDialog } from '../components/confirm-dialog.js'
import { AgentDiffTable } from '../components/diff-view.js'
import { ErrorPanel } from '../components/error-panel.js'
import { CheckboxField, Field, SelectField, TextareaField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import {
  bindingsOf,
  bindingsPayload,
  emptyBindings,
  type PromptBindings,
} from '../lib/agent-bindings.js'
import { formatDateTime, formatRelative } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

export function AgentPage() {
  const workspace = useCurrentWorkspace()
  const { agentId = '' } = useParams()
  const queryClient = useQueryClient()

  const canEdit = roleAtLeast(workspace.role, 'admin')

  const agent = useQuery({
    queryKey: ['agent', workspace.id, agentId],
    queryFn: () => fetchAgent(workspace.id, agentId),
  })

  const prompts = useQuery({
    queryKey: ['prompts', workspace.id],
    queryFn: () => fetchPrompts(workspace.id),
  })

  const channels = useQuery({
    queryKey: ['channels', workspace.id],
    queryFn: () => fetchChannels(workspace.id),
  })

  const [draft, setDraft] = useState<AgentConfig | null>(null)

  /**
   * Список запрещённых оборотов живёт в двух видах сразу.
   *
   * В настройках это массив строк, а в поле ввода — текст, и одного
   * массива тут мало: если рисовать поле из него, то Enter в конце
   * списка исчезает в ту же секунду, потому что пустая строка в массив
   * не попадает. Человек жмёт Enter и ничего не происходит — поле
   * выглядит сломанным. Поэтому набранный текст хранится как есть, а
   * массив пересобирается из него на каждое изменение.
   */
  const [forbiddenText, setForbiddenText] = useState<string | null>(null)
  /**
   * Паузы напоминаний живут в двух видах, как и запрещённые обороты:
   * в настройках это числа, а в поле — текст. Без текста запятая
   * исчезала бы в ту же секунду, как её набрали: «60,» в массив не
   * превращается, и поле выглядело бы сломанным.
   */
  const [delaysText, setDelaysText] = useState<string | null>(null)
  /**
   * Привязка на каждую роль, а не одна системная.
   *
   * До 25.09.2026 здесь лежал единственный `promptVersionId`, и форма
   * отправляла на сохранение массив ровно из одной записи. Сервер
   * заменяет привязки целиком тем, что пришло, — значит привязка
   * проверки ответа или готовности к звонку, появись она в базе, была бы
   * стёрта первым же сохранением агента, молча и без следа. А появиться
   * ей было неоткуда: выбрать эти роли на экране было нельзя вовсе.
   * Отсюда и шаг «проверка без модели» в каждом живом прогоне.
   */
  const [bindings, setBindings] = useState<PromptBindings>(emptyBindings)
  const [note, setNote] = useState('')
  const [compare, setCompare] = useState<{ from: string; to: string } | null>(null)
  const [toPublish, setToPublish] = useState<AgentVersionSummary | null>(null)
  const [sameNotice, setSameNotice] = useState(false)

  const publishedVersionId = agent.data?.publishedVersion?.id ?? null

  /**
   * Черновик перезаполняется, когда сменилась опубликованная версия, —
   * та же причина, что на экране промпта. Зависимость от «черновик пуст»
   * подставляла бы ещё не обновившиеся данные сразу после публикации,
   * и форма показывала бы прежние настройки как текущие.
   */
  useEffect(() => {
    if (!agent.data) return

    setDraft(agent.data.currentConfig)
    setForbiddenText(null)
    setDelaysText(null)
    /**
     * Берётся опубликованная версия, а при её отсутствии — последняя
     * созданная. Иначе у неопубликованного агента форма показывала бы
     * пустоту поверх настоящих привязок и стёрла бы их при сохранении.
     */
    setBindings(bindingsOf(agent.data.publishedVersion ?? agent.data.versions[0]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedVersionId])

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['agent', workspace.id, agentId] }),
      queryClient.invalidateQueries({ queryKey: ['agents', workspace.id] }),
      queryClient.invalidateQueries({ queryKey: ['channels', workspace.id] }),
    ])

  const promptsPayload = bindingsPayload(bindings)

  const save = useMutation({
    mutationFn: (input: { config: AgentConfigPatch; publish: boolean }) =>
      createAgentVersion(workspace.id, agentId, {
        config: input.config,
        /**
         * Привязки уходят полным списком: сервер заменяет их целиком
         * тем, что пришло. Сборка списка — в `agent-bindings.ts`, там же
         * проверки: ошибка в этом месте не видна ни в типах, ни глазами
         * на экране, а стоит целой настройки агента.
         */
        ...(promptsPayload ? { prompts: promptsPayload } : {}),
        note,
        publish: input.publish,
      }),
    onSuccess: (result) => {
      setNote('')
      setSameNotice(!result.created)
      void invalidate()
    },
  })

  const publish = useMutation({
    mutationFn: (versionId: string) => publishAgentVersion(workspace.id, agentId, versionId),
    onSuccess: () => {
      setToPublish(null)
      void invalidate()
    },
  })

  const bind = useMutation({
    mutationFn: (input: { channelId: string; agentId: string | null }) =>
      bindChannelAgent(workspace.id, input.channelId, input.agentId),
    onSuccess: () => void invalidate(),
  })

  const diff = useQuery({
    queryKey: ['agent-diff', workspace.id, agentId, compare?.from, compare?.to],
    queryFn: () => fetchAgentDiff(workspace.id, agentId, compare?.from ?? '', compare?.to ?? ''),
    enabled: compare !== null,
  })

  if (agent.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        <Spinner /> Загружаем агента…
      </div>
    )
  }

  if (agent.error || !agent.data) {
    return <ErrorPanel error={agent.error} onRetry={() => void agent.refetch()} />
  }

  const data = agent.data
  const published = data.publishedVersion
  const config = draft ?? data.currentConfig

  const promptOptions = (prompts.data ?? []).flatMap((prompt) =>
    prompt.publishedVersion
      ? [
          {
            id: prompt.publishedVersion.id,
            label: `${prompt.name} · версия ${prompt.publishedVersion.version} (опубликована)`,
          },
        ]
      : [],
  )

  /**
   * Закреплённая версия может быть уже не опубликованной — именно ради
   * этого случая всё и затевалось. Если её нет в списке, она добавляется
   * отдельным пунктом: иначе форма молча переключила бы агента на другой
   * текст при первом же сохранении. Проверяются привязки всех ролей, а
   * не только системной: у остальных та же история версий.
   */
  for (const pinned of published?.prompts ?? []) {
    if (promptOptions.some((option) => option.id === pinned.promptVersionId)) continue

    promptOptions.unshift({
      id: pinned.promptVersionId,
      label: `${pinned.promptName} · версия ${pinned.promptVersion} (закреплена, не опубликована)`,
    })
  }

  /**
   * Проверка ответа моделью включается не переключателем, а наличием
   * промпта: без текста проверять нечем. Совпадение «галка стоит, а
   * промпта нет» выглядит как работающая проверка и ею не является,
   * поэтому сказано прямо, здесь же, а не в документации.
   */
  const validationHalfOn = config.validation.enabled && !bindings.validation

  return (
    <>
      <PageHeader
        title={data.name}
        description={
          <>
            {data.description || 'Без пояснения'} ·{' '}
            <Link to={`/w/${workspace.id}/agents`} className="text-accent hover:underline">
              ко всем агентам
            </Link>
          </>
        }
        actions={
          published ? (
            <Badge tone="ok">опубликована версия {published.version}</Badge>
          ) : (
            <Badge tone="warn">ни одна версия не опубликована</Badge>
          )
        }
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-col gap-5">
          <Panel
            title="Настройки"
            description={
              canEdit
                ? 'Сохранение создаёт следующую версию — прежние остаются нетронутыми.'
                : 'Править агентов может администратор воркспейса.'
            }
          >
            <div className="flex flex-col gap-4">
              <SelectField
                label={AGENT_CONFIG_FIELD_LABELS['model'] ?? 'Модель'}
                value={config.model}
                disabled={!canEdit}
                onChange={(event) => setDraft({ ...config, model: event.target.value })}
                hint="Список моделей у OpenAI меняется чаще релизов, поэтому имя — строка, а не перечисление"
              >
                {modelOptions(config.model).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </SelectField>

              <SelectField
                label={AGENT_CONFIG_FIELD_LABELS['serviceModel'] ?? 'Модель для служебных вызовов'}
                value={config.serviceModel ?? ''}
                disabled={!canEdit}
                onChange={(event) =>
                  setDraft({ ...config, serviceModel: event.target.value || null })
                }
                hint="Проверка ответа и проверка готовности к звонку. Клиент этих вызовов не видит: одна отвечает «да» или «нет», другая — одним словом. Качество письма там не нужно, а платим мы как за обычный ответ, и на каждый ответ приходится до двух таких вызовов. Разница между моделями поколения — двадцатикратная."
              >
                <option value="">как основная ({config.model})</option>
                {modelOptions(config.serviceModel ?? '')
                  .filter((value) => value !== '')
                  .map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
              </SelectField>

              {PROMPT_ROLES.map((role) => {
                const required = REQUIRED_PROMPT_ROLES.includes(role)

                return (
                  <SelectField
                    key={role}
                    label={PROMPT_ROLE_LABELS[role]}
                    value={bindings[role] ?? ''}
                    disabled={!canEdit}
                    onChange={(event) =>
                      setBindings({ ...bindings, [role]: event.target.value || null })
                    }
                    hint={PROMPT_ROLE_HINTS[role]}
                  >
                    {required ? (
                      promptOptions.length === 0 && (
                        <option value="">нет опубликованных промптов</option>
                      )
                    ) : (
                      <option value="">не привязан — этот шаг не выполняется</option>
                    )}
                    {promptOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </SelectField>
                )
              })}

              <SelectField
                label="Что агент видит из переписки"
                value={config.history.kind}
                disabled={!canEdit}
                onChange={(event) => setDraft({ ...config, history: historyFor(event.target.value) })}
                hint="Вариант «всю переписку» есть и работает. Он не стоит по умолчанию потому, что история заново оплачивается при каждом ответе: в диалоге на сто сообщений сто первый ответ тащит за собой все сто. Это единственный рычаг, которым этот рост управляется."
              >
                <option value="lastMessages">Последние N сообщений</option>
                <option value="lastMinutes">Последние N минут</option>
                <option value="all">Всю переписку</option>
                <option value="none">Ничего</option>
              </SelectField>

              {config.history.kind === 'lastMessages' && (
                <NumberField
                  label={AGENT_CONFIG_FIELD_LABELS['history.count'] ?? 'История: сообщений'}
                  value={config.history.count}
                  disabled={!canEdit}
                  onChange={(value) =>
                    setDraft({ ...config, history: { kind: 'lastMessages', count: value } })
                  }
                  hint="По умолчанию 40 — ровно столько же, сколько предел сообщений на диалог ниже. При этих значениях агент видит переписку целиком, просто она не может вырасти длиннее"
                />
              )}

              {config.history.kind === 'lastMinutes' && (
                <NumberField
                  label={AGENT_CONFIG_FIELD_LABELS['history.minutes'] ?? 'История: минут'}
                  value={config.history.minutes}
                  disabled={!canEdit}
                  onChange={(value) =>
                    setDraft({ ...config, history: { kind: 'lastMinutes', minutes: value } })
                  }
                  hint="Отсчёт от последнего сообщения. Клиент, вернувшийся через сутки, для агента начинает разговор заново"
                />
              )}

              <div className="grid gap-4 md:grid-cols-2">
                {/*
                  Температуры здесь больше нет — убрана 24.09.2026.
                  Причина в `docs/DECISIONS.md`, коротко: на нашей модели
                  она не работает вовсе, а там, где работает, разнообразие
                  формулировок надёжнее задавать инструкцией, которую видно.
                  Значение осталось в настройках и в версиях — исчезла
                  только ручка.
                */}
                <NumberField
                  label={AGENT_CONFIG_FIELD_LABELS['maxIterations'] ?? 'Предел проходов'}
                  value={config.maxIterations}
                  disabled={!canEdit}
                  onChange={(value) => setDraft({ ...config, maxIterations: value })}
                  hint="В старой системе было 25, снизили до 7 после перерасхода бюджета"
                />
                <NumberField
                  label="Максимум сообщений на диалог"
                  value={config.limits.maxMessagesPerConversation}
                  disabled={!canEdit}
                  onChange={(value) =>
                    setDraft({
                      ...config,
                      limits: { ...config.limits, maxMessagesPerConversation: value },
                    })
                  }
                  hint="Достигнув предела, агент замолкает и диалог уходит к оператору"
                />
                <NumberField
                  label="Максимальная стоимость диалога, $"
                  value={config.limits.maxCostPerConversationUsd}
                  step={0.1}
                  disabled={!canEdit}
                  onChange={(value) =>
                    setDraft({
                      ...config,
                      limits: { ...config.limits, maxCostPerConversationUsd: value },
                    })
                  }
                  hint="Переключив историю на «всю переписку», поднимите и этот потолок — иначе агент упрётся в него раньше, чем клиент договорит"
                />
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-line bg-raised/40 p-4">
                <div>
                  <p className="text-sm font-semibold text-ink">Как агент пишет</p>
                  <p className="mt-1 text-xs text-muted">
                    Две настройки про то, чтобы разговор не выглядел машинным: одна —
                    сколько ждать, если клиент пишет частями, вторая — присылать ли
                    длинный ответ не простынёй.
                  </p>
                </div>

                <NumberField
                  label="Ждать продолжения, мс"
                  value={config.debounceMs}
                  step={500}
                  disabled={!canEdit}
                  onChange={(value) => setDraft({ ...config, debounceMs: value })}
                  hint="Человек пишет так: «привет», «а сколько стоит», «и когда доставка». Агент выжидает эту паузу и отвечает один раз на всё. Ноль — отвечать на каждое сообщение отдельно, как робот"
                />

                <CheckboxField
                  label={AGENT_CONFIG_FIELD_LABELS['splitReply.enabled'] ?? 'Разбивать ответ'}
                  checked={config.splitReply.enabled}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setDraft({
                      ...config,
                      splitReply: { ...config.splitReply, enabled: event.target.checked },
                    })
                  }
                  hint="Абзацы ответа уходят отдельными сообщениями, как пишет живой человек. Платится надёжностью: две отправки вместо одной — два шанса на сбой шлюза"
                />

                <NumberField
                  label={AGENT_CONFIG_FIELD_LABELS['splitReply.delayMs'] ?? 'Пауза между частями, мс'}
                  value={config.splitReply.delayMs}
                  step={500}
                  disabled={!canEdit || !config.splitReply.enabled}
                  onChange={(value) =>
                    setDraft({ ...config, splitReply: { ...config.splitReply, delayMs: value } })
                  }
                  hint="В эту паузу клиент успевает написать. Если напишет — остаток не отправляется: агент не должен договаривать поверх человека, сказавшего «стоп»"
                />
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-line bg-raised/40 p-4">
                <div>
                  <p className="text-sm font-semibold text-ink">Напоминания</p>
                  <p className="mt-1 text-xs text-muted">
                    Клиент спросил цену и пропал — живой продавец в этом месте пишет ещё
                    раз. Паузы считаются от последнего сообщения агента; ответ клиента
                    отменяет напоминание и начинает счёт заново.
                  </p>
                </div>

                <CheckboxField
                  label={AGENT_CONFIG_FIELD_LABELS['followUp.enabled'] ?? 'Напоминать'}
                  checked={config.followUp.enabled}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setDraft({
                      ...config,
                      followUp: { ...config.followUp, enabled: event.target.checked },
                    })
                  }
                  hint="Выключено по умолчанию нарочно: напоминание — сообщение, которого клиент не просил, и включать такое за вас никто не должен"
                />

                <Field
                  label={AGENT_CONFIG_FIELD_LABELS['followUp.delaysMinutes'] ?? 'Паузы, мин'}
                  value={delaysText ?? config.followUp.delaysMinutes.join(', ')}
                  disabled={!canEdit || !config.followUp.enabled}
                  onChange={(event) => {
                    setDelaysText(event.target.value)
                    setDraft({
                      ...config,
                      followUp: { ...config.followUp, delaysMinutes: toMinutes(event.target.value) },
                    })
                  }}
                  hint="Через запятую, до пяти штук. 60, 1440 — напомнить через час, потом через сутки. Сколько чисел, столько напоминаний: на последнее берётся промпт «Последний шаг follow-up», на остальные — «Шаг follow-up»"
                />

                <Field
                  label={
                    AGENT_CONFIG_FIELD_LABELS['followUp.lastStepMinutes'] ?? 'Последнее, мин'
                  }
                  type="number"
                  min={0}
                  max={20_160}
                  value={config.followUp.lastStepMinutes}
                  disabled={!canEdit || !config.followUp.enabled}
                  onChange={(event) =>
                    setDraft({
                      ...config,
                      followUp: {
                        ...config.followUp,
                        lastStepMinutes: Number(event.target.value) || 0,
                      },
                    })
                  }
                  hint="Последняя попытка — отсчитывается от начала разговора, а не от молчания, и уходит один раз. Смысл в сроке: через сутки после первого сообщения клиента WhatsApp закрывает окно, и написать первыми будет уже нельзя. 1380 — за час до закрытия. Ноль — не отправлять"
                />

                <CheckboxField
                  label={AGENT_CONFIG_FIELD_LABELS['followUp.repeatable'] ?? 'Начинать заново'}
                  checked={config.followUp.repeatable}
                  disabled={!canEdit || !config.followUp.enabled}
                  onChange={(event) =>
                    setDraft({
                      ...config,
                      followUp: { ...config.followUp, repeatable: event.target.checked },
                    })
                  }
                  hint="Ответил — и цепочка считается с первого напоминания. Снимите, если не хотите, чтобы разговорчивый клиент получал напоминания снова и снова"
                />

                <div className="grid gap-3 sm:grid-cols-3">
                  <Field
                    label={AGENT_CONFIG_FIELD_LABELS['followUp.window.startHour'] ?? 'С часа'}
                    type="number"
                    min={0}
                    max={23}
                    value={config.followUp.window.startHour}
                    disabled={!canEdit || !config.followUp.enabled}
                    onChange={(event) =>
                      setDraft({
                        ...config,
                        followUp: {
                          ...config.followUp,
                          window: {
                            ...config.followUp.window,
                            startHour: Number(event.target.value) || 0,
                          },
                        },
                      })
                    }
                  />
                  <Field
                    label={AGENT_CONFIG_FIELD_LABELS['followUp.window.endHour'] ?? 'До часа'}
                    type="number"
                    min={0}
                    max={24}
                    value={config.followUp.window.endHour}
                    disabled={!canEdit || !config.followUp.enabled}
                    onChange={(event) =>
                      setDraft({
                        ...config,
                        followUp: {
                          ...config.followUp,
                          window: {
                            ...config.followUp.window,
                            endHour: Number(event.target.value) || 0,
                          },
                        },
                      })
                    }
                  />
                  <Field
                    label={AGENT_CONFIG_FIELD_LABELS['followUp.window.timezone'] ?? 'Часовой пояс'}
                    value={config.followUp.window.timezone}
                    disabled={!canEdit || !config.followUp.enabled}
                    onChange={(event) =>
                      setDraft({
                        ...config,
                        followUp: {
                          ...config.followUp,
                          window: { ...config.followUp.window, timezone: event.target.value },
                        },
                      })
                    }
                  />
                </div>

                <p className="text-xs text-muted">
                  Часы — по времени клиента, поэтому нужен его пояс: `America/Guatemala`,
                  `America/Lima`. Не смещение числом — оно меняется дважды в год. Равные
                  часы означают «круглые сутки». Без окна напоминание уходит в три часа
                  ночи, и это единственная причина, по которой окно здесь есть.
                </p>

                {config.followUp.enabled &&
                  config.followUp.window.startHour === config.followUp.window.endHour && (
                    <p className="rounded-md border border-copper/30 bg-copper-soft px-3 py-2 text-xs text-ink-soft">
                      Окно не задано — напоминания будут уходить круглосуточно, в том числе
                      ночью по времени клиента.
                    </p>
                  )}

                {config.followUp.enabled && !bindings.followup_step && (
                  <p className="rounded-md border border-copper/30 bg-copper-soft px-3 py-2 text-xs text-ink-soft">
                    Напоминания не будут отправляться: промпт роли «Шаг follow-up» не
                    привязан выше. Текст напоминания система за вас не придумывает — это
                    сообщение настоящему клиенту от вашего имени.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4 rounded-lg border border-line bg-raised/40 p-4">
                <div>
                  <p className="text-sm font-semibold text-ink">Проверка ответа</p>
                  <p className="mt-1 text-xs text-muted">
                    Последний заслон между моделью и клиентом. Готовый текст проверяется
                    до отправки; не прошёл — агенту даётся одна поправка, и если она
                    не помогла, агент молчит, а диалог уходит оператору.
                  </p>
                </div>

                <CheckboxField
                  label={AGENT_CONFIG_FIELD_LABELS['validation.enabled'] ?? 'Проверять ответ'}
                  checked={config.validation.enabled}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setDraft({
                      ...config,
                      validation: { ...config.validation, enabled: event.target.checked },
                    })
                  }
                  hint="Сняв галочку, вы отключаете все проверки разом — и длину, и обороты, и проверку моделью. Клиент увидит первое, что напишет модель"
                />

                {validationHalfOn && (
                  <p className="rounded-md border border-copper/30 bg-copper-soft px-3 py-2 text-xs text-ink-soft">
                    Проверка моделью сейчас не работает: промпт роли «Проверка ответа» не привязан
                    выше. Работают только проверки без модели — длина, запрещённые обороты,
                    незаполненные подстановки.
                  </p>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <NumberField
                    label={
                      AGENT_CONFIG_FIELD_LABELS['validation.maxLength'] ?? 'Предел длины, знаков'
                    }
                    value={config.validation.maxLength}
                    disabled={!canEdit || !config.validation.enabled}
                    onChange={(value) =>
                      setDraft({
                        ...config,
                        validation: { ...config.validation, maxLength: value },
                      })
                    }
                    hint="От 50 до 4096. Это не предел WhatsApp, а предел читаемости: в окно влезает 4096 знаков, но простыню никто не дочитает, и живой продавец так не пишет"
                  />
                  <NumberField
                    label={
                      AGENT_CONFIG_FIELD_LABELS['validation.maxAttempts'] ?? 'Попыток исправить'
                    }
                    value={config.validation.maxAttempts}
                    disabled={!canEdit || !config.validation.enabled}
                    onChange={(value) =>
                      setDraft({
                        ...config,
                        validation: { ...config.validation, maxAttempts: value },
                      })
                    }
                    hint="От 1 до 3. Каждая попытка — это ещё один платный ответ модели. В старой системе предела не было, и спор агента с проверяющим съел пятикратный бюджет за три дня"
                  />
                  <NumberField
                    label={
                      AGENT_CONFIG_FIELD_LABELS['validation.minScore'] ?? 'Оценка не ниже'
                    }
                    value={config.validation.minScore}
                    disabled={!canEdit || !config.validation.enabled}
                    onChange={(value) =>
                      setDraft({
                        ...config,
                        validation: { ...config.validation, minScore: value },
                      })
                    }
                    hint="Проверяющая модель ставит ответу оценку от 0 до 10 и пишет список правок. Десять — как в старой системе: любое замечание отправляет ответ на второй круг. Ноль отключает шкалу, и решает само наличие замечаний. Оценка пишется в шаги прогона всегда, так что порог можно подобрать по живым диалогам"
                  />
                </div>

                {config.validation.enabled && config.validation.minScore === 10 && (
                  <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-muted">
                    При десятке почти каждый ответ идёт на второй круг — это примерно
                    вдвое дороже. Так работала старая система, отработавшая пятнадцать
                    тысяч задач. Посмотрите оценки в «Как получился ответ» на карточке
                    диалога: если девятки там частые и придирки мелкие, порог можно
                    опустить.
                  </p>
                )}

                <TextareaField
                  label={
                    AGENT_CONFIG_FIELD_LABELS['validation.forbidden'] ?? 'Запрещённые обороты'
                  }
                  value={forbiddenText ?? config.validation.forbidden.join('\n')}
                  rows={5}
                  disabled={!canEdit || !config.validation.enabled}
                  onChange={(event) => {
                    setForbiddenText(event.target.value)
                    setDraft({
                      ...config,
                      validation: { ...config.validation, forbidden: toPhrases(event.target.value) },
                    })
                  }}
                  hint="По одному обороту на строку, до пятидесяти. Регистр не важен: «Гарантирую» поймается правилом «гарантирую». Сюда пишут то, что дорого стоит, если сорвётся с языка, — обещания сроков, гарантий и скидок, которых никто не давал"
                />

                {config.validation.forbidden.length >= 50 && (
                  <p className="text-xs text-copper">
                    Больше пятидесяти оборотов не сохранится — лишние строки отброшены.
                  </p>
                )}
              </div>

              {canEdit && (
                <>
                  <Field
                    label="Что изменилось"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={300}
                    placeholder="Снизил температуру, подтянул новый промпт"
                  />

                  {sameNotice && (
                    <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-muted">
                      Настройки не изменились, новая версия не создана.
                    </p>
                  )}

                  {save.error != null && <ErrorPanel error={save.error} />}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      loading={save.isPending}
                      onClick={() => save.mutate({ config, publish: true })}
                    >
                      Сохранить и опубликовать
                    </Button>
                    <Button
                      loading={save.isPending}
                      onClick={() => save.mutate({ config, publish: false })}
                    >
                      Сохранить как черновик
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setDraft(null)
                        setForbiddenText(null)
                      }}
                    >
                      Вернуть опубликованные настройки
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Panel>

          <Panel
            title="Каналы"
            description="Один канал — один агент. Канал без агента принимает сообщения и не отвечает."
          >
            {channels.isLoading && <p className="text-sm text-muted">Загружаем каналы…</p>}
            {channels.data?.length === 0 && (
              <p className="text-sm text-muted">Каналов пока нет.</p>
            )}

            <ul className="flex flex-col gap-2">
              {(channels.data ?? []).map((channel) => (
                <li
                  key={channel.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-raised px-3 py-2 text-sm"
                >
                  <span className="text-ink">
                    {channel.name}
                    <span className="ml-2 text-xs text-muted">
                      {channel.agentId === null
                        ? 'агент не назначен'
                        : channel.agentId === agentId
                          ? 'этот агент'
                          : `агент «${channel.agentName ?? '—'}»`}
                    </span>
                  </span>

                  {canEdit && (
                    <Button
                      size="sm"
                      loading={bind.isPending}
                      onClick={() =>
                        bind.mutate({
                          channelId: channel.id,
                          agentId: channel.agentId === agentId ? null : agentId,
                        })
                      }
                    >
                      {channel.agentId === agentId ? 'Отвязать' : 'Назначить этого агента'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>

            {bind.error != null && <ErrorPanel error={bind.error} className="mt-3" />}
          </Panel>

          {compare && (
            <Panel
              title="Сравнение версий"
              actions={
                <Button size="sm" variant="ghost" onClick={() => setCompare(null)}>
                  Закрыть
                </Button>
              }
            >
              {diff.isLoading && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Spinner /> Считаем разницу…
                </p>
              )}
              {diff.error != null && <ErrorPanel error={diff.error} />}
              {diff.data && <AgentDiffTable diff={diff.data} />}
            </Panel>
          )}
        </div>

        <Panel
          title="История версий"
          description="Публикация и откат — одно действие: переставить указатель"
        >
          <ol className="flex flex-col gap-2">
            {data.versions.map((version) => (
              <li
                key={version.id}
                className="rounded-md border border-line bg-raised px-3 py-2.5 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-ink">Версия {version.version}</span>
                  {version.isPublished && <Badge tone="ok">опубликована</Badge>}
                </div>

                <p className="mt-0.5 text-xs text-muted">
                  {formatRelative(version.createdAt)} · {version.createdByEmail ?? 'автор удалён'}
                </p>

                {version.prompts.map((binding) => (
                  <p key={binding.role} className="mt-1 text-xs text-ink-soft">
                    {binding.promptName} · версия {binding.promptVersion}
                  </p>
                ))}

                {version.note && <p className="mt-1 text-xs text-ink-soft">{version.note}</p>}

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {published && published.id !== version.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      /* от опубликованной к выбранной — см. экран промпта */
                      onClick={() => setCompare({ from: published.id, to: version.id })}
                    >
                      Сравнить с опубликованной
                    </Button>
                  )}

                  {canEdit && !version.isPublished && (
                    <Button size="sm" onClick={() => setToPublish(version)}>
                      {published && version.version < published.version
                        ? 'Откатить сюда'
                        : 'Опубликовать'}
                    </Button>
                  )}
                </div>

                <p className="mt-1 text-[11px] text-muted">{formatDateTime(version.createdAt)}</p>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      <ConfirmDialog
        open={toPublish !== null}
        onOpenChange={(open) => !open && setToPublish(null)}
        title={`Опубликовать версию ${toPublish?.version ?? ''}?`}
        description={
          published && toPublish && toPublish.version < published.version
            ? `Это откат: сейчас опубликована версия ${published.version}. Диалоги, которые уже идут, останутся на закреплённой за ними версии — публикация действует на новые.`
            : 'Новые диалоги пойдут по этой версии. Уже идущие останутся на закреплённой за ними.'
        }
        confirmLabel="Опубликовать"
        loading={publish.isPending}
        onConfirm={() => toPublish && publish.mutate(toPublish.id)}
      />

      {publish.error != null && <ErrorPanel error={publish.error} className="mt-4" />}
    </>
  )
}

/**
 * Смена режима истории через выпадающий список.
 *
 * Режим — размеченное объединение: у «последних N сообщений» есть число,
 * у «всей переписки» его нет. Поэтому при переключении собирается целое
 * значение со значением по умолчанию, а не правится поле у прежнего:
 * половина одного варианта, приклеенная к другому, схему не пройдёт.
 *
 * Значения по умолчанию совпадают с теми, что записываются при создании
 * агента, — человек, переключивший режим туда и обратно, получит ровно
 * то, с чего начал.
 */
/**
 * Что делает каждая роль — словами человека, который будет это выбирать.
 *
 * Важнее всего в этих подсказках не описание, а следствие: непривязанный
 * промпт означает, что шаг не выполняется вовсе. Пустое поле выглядит
 * как «настройка по умолчанию», а на деле это выключатель.
 */
const PROMPT_ROLE_HINTS: Record<PromptRole, string> = {
  system: 'Кем агент себя считает и как ведёт разговор. Закрепляется версией: правка текста промпта не меняет поведение этого агента задним числом',
  validation:
    'Читает ответ агента перед отправкой и решает, можно ли его слать. Пока промпт не привязан, работают только проверки без модели — длина, запрещённые обороты, незаполненные подстановки, — а переключатель «Проверять ответ перед отправкой» сам по себе модель не включает',
  readiness:
    'Смотрит на весь разговор и решает, дозрел ли клиент до звонка продавца. Без него исход «передан в колл-центр» агент может поставить только инструментом, то есть по собственному решению в переписке',
  followup_step: 'Чем напомнить о себе, если клиент замолчал. Шагов может быть несколько',
  followup_last: 'Последнее напоминание перед тем, как оставить клиента в покое',
}

/**
 * Что показать в выпадающем списке моделей.
 *
 * Список — подсказка, а не ограничение: у OpenAI он меняется чаще, чем
 * выходят наши релизы, и имя модели поэтому хранится строкой. Здесь
 * перечислены те, на которые у нас заданы цены в `MODEL_PRICES`; при
 * появлении новой её дописывают сюда и в `.env` одновременно — иначе
 * стоимость диалога станет неизвестной, а вместе с ней перестанет
 * применяться предел расходов.
 *
 * Текущее значение подставляется первым всегда, даже если его в списке
 * нет: иначе открытие страницы молча заменило бы модель на чужую.
 */
function modelOptions(current: string): string[] {
  const known = ['gpt-6-sol', 'gpt-6-luna', 'gpt-6-astra', 'gpt-5.6-sol']

  return [current, ...known].filter(
    (value, index, all) => value !== '' && all.indexOf(value) === index,
  )
}

/**
 * Числа из строки «60, 1440».
 *
 * Пустые куски отбрасываются, нечисловые тоже: человек набирает запятую
 * раньше следующего числа, и в этот момент строка заведомо неполна.
 * Схема всё равно проверит границы при сохранении — здесь задача одна:
 * не ломать набор.
 */
function toMinutes(value: string): number[] {
  return value
    .split(',')
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => Number.isFinite(item) && item > 0)
    .slice(0, 5)
}

function historyFor(kind: string): HistoryMode {
  if (kind === 'all') return { kind: 'all' }
  if (kind === 'none') return { kind: 'none' }
  if (kind === 'lastMinutes') return { kind: 'lastMinutes', minutes: 1_440 }
  return { kind: 'lastMessages', count: 40 }
}

function NumberField({
  label,
  value,
  onChange,
  step,
  disabled,
  hint,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  step?: number
  disabled?: boolean
  hint?: string
}) {
  return (
    <Field
      label={label}
      type="number"
      step={step ?? 1}
      value={String(value)}
      disabled={disabled ?? false}
      {...(hint ? { hint } : {})}
      onChange={(event) => {
        const parsed = Number(event.target.value)

        /**
         * Пустое поле и мусор не превращаются в ноль. Ноль здесь —
         * осмысленное значение для части настроек, и подставлять его
         * молча значило бы записать в версию то, чего человек не вводил.
         */
        if (Number.isFinite(parsed)) onChange(parsed)
      }}
    />
  )
}

/**
 * Строки поля ввода в список оборотов.
 *
 * Обрезка здесь, а не только на сервере, намеренно: правила те же, что
 * в схеме (не пустые, не длиннее ста знаков, не больше пятидесяти), но
 * человек видит их действие сразу, а не получает отказ после нажатия
 * «Сохранить». Пустые строки между оборотами при этом набирать можно —
 * они просто не попадают в список.
 */
function toPhrases(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim().slice(0, 100))
    .filter((line) => line !== '')
    .slice(0, 50)
}
